import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputBase,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
  LinearProgress,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  FileCopy as DuplicateIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  NotificationsActive as ReminderIcon,
} from "@mui/icons-material";
import { FormattedMessage } from "react-intl";
import { colors, routes } from "../../constants";
import ReminderScheduleDateTimePopover from "./ReminderScheduleDateTimePopover";

const formatCompactDate = (dateString) => {
  try {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}/${month}/${day}`;
  } catch (error) {
    return dateString;
  }
};

const transformStatusForDisplay = (status) => {
  if (status?.toLowerCase() === "completed") return "LOCKED";
  return status;
};

const getCollectorCardStyles = (statusStyle) => ({
  borderRadius: "16px",
  boxShadow: `0 8px 32px ${statusStyle.shadowColor}`,
  transition: "transform 0.3s, box-shadow 0.3s",
  border: `3px solid ${statusStyle.borderColor}`,
  background: `linear-gradient(135deg, ${statusStyle.bgColor} 0%, rgba(255,255,255,0.95) 100%)`,
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    backgroundColor: statusStyle.borderColor,
    borderRadius: "16px 16px 0 0",
  },
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: `0 12px 40px ${statusStyle.shadowColor}`,
  },
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: "250px",
});

const Styles = {
  cardContent: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    p: 2,
    pb: 1,
  },
  collectorCardTitle: {
    fontWeight: "bold",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    pr: 2,
    fontSize: "1.1rem",
  },
  collectorCardDate: {
    color: "text.secondary",
    fontSize: "0.7rem",
    lineHeight: 1.2,
    mt: 0.25,
  },
  collectorMetricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 1,
    p: 1.5,
    mt: 1.5,
    backgroundColor: "rgba(255,255,255,0.7)",
    borderRadius: "8px",
    border: "1px solid rgba(0,0,0,0.05)",
  },
};

export default function CollectorCard({
  collector,
  surveyId,
  surveyStatus,
  statusStyle,
  overallStatus,
  onLockClick,
  onDeleteClick,
  onEditClick,
  onCopyLink,
  onViewFailedEmails,
  onDuplicateClick,
  onRenameCollector,
  onReminderClick,
}) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(collector.name);
  const [isSavingRename, setIsSavingRename] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [reminderMode, setReminderMode] = useState("now");
  const [scheduledTimeStr, setScheduledTimeStr] = useState(null);
  const [schedulePickerAnchor, setSchedulePickerAnchor] = useState(null);
  const inputRef = useRef(null);

  const resetReminderDialog = () => {
    setReminderMode("now");
    setScheduledTimeStr(null);
    setSchedulePickerAnchor(null);
  };

  const closeReminderDialog = () => {
    if (isSendingReminder) return;
    setReminderDialogOpen(false);
    resetReminderDialog();
  };

  // Keep local rename value in sync if the collector name changes externally
  useEffect(() => {
    if (!isRenaming) setRenameValue(collector.name);
  }, [collector.name, isRenaming]);

  const startRenaming = () => {
    setRenameValue(collector.name);
    setIsRenaming(true);
  };

  const cancelRenaming = () => {
    setRenameValue(collector.name);
    setIsRenaming(false);
  };

  const commitRename = async () => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === collector.name) {
      cancelRenaming();
      return;
    }
    setIsSavingRename(true);
    try {
      await onRenameCollector(collector.id, trimmed);
      setIsRenaming(false);
    } catch {
      // keep the input open so the user can retry or cancel
    } finally {
      setIsSavingRename(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") commitRename();
    if (e.key === "Escape") cancelRenaming();
  };

  const handleConfirmReminder = async () => {
    if (reminderMode === "schedule" && !scheduledTimeStr) {
      return;
    }
    setIsSendingReminder(true);
    try {
      await onReminderClick(
        collector.id,
        reminderMode === "schedule" && scheduledTimeStr ? { scheduledTime: scheduledTimeStr } : {}
      );
      setReminderDialogOpen(false);
      resetReminderDialog();
    } catch {
      // keep dialog open so user can retry
    } finally {
      setIsSendingReminder(false);
    }
  };

  // Auto-focus the input when entering rename mode
  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  return (
    <Card sx={getCollectorCardStyles(statusStyle)}>
      <CardContent sx={Styles.cardContent}>
        {/* Header with Title and Status Badges */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0, flexGrow: 1, minWidth: 0 }}>
              {isRenaming ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <InputBase
                    inputRef={inputRef}
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isSavingRename}
                    sx={{
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      color: colors.main,
                      flexGrow: 1,
                      minWidth: 0,
                      borderBottom: "2px solid",
                      borderColor: "primary.main",
                      px: 0.5,
                      "& input": { p: 0 },
                    }}
                  />
                  {isSavingRename ? (
                    <CircularProgress size={16} sx={{ ml: 0.5 }} />
                  ) : (
                    <>
                      <Tooltip title={<FormattedMessage id="collector.rename_save" defaultMessage="Save" />}>
                        <IconButton size="small" onClick={commitRename} sx={{ color: "success.main", p: 0.25 }}>
                          <CheckIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={<FormattedMessage id="collector.rename_cancel" defaultMessage="Cancel" />}>
                        <IconButton size="small" onClick={cancelRenaming} sx={{ color: "error.main", p: 0.25 }}>
                          <CloseIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={[Styles.collectorCardTitle, { color: colors.main }]}>
                    {collector.name}
                  </Typography>
                  <Tooltip title={<FormattedMessage id="collector.rename_collector" defaultMessage="Rename" />}>
                    <IconButton
                      size="small"
                      onClick={startRenaming}
                      sx={{ color: "text.secondary", p: 0.25, flexShrink: 0, "&:hover": { color: "primary.main" } }}
                    >
                      <EditIcon sx={{ fontSize: "0.85rem" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              <Typography variant="body2" sx={Styles.collectorCardDate}>
                {formatCompactDate(collector.created_at)}
              </Typography>
            </Box>
          </Box>

          <Chip
            label={`${transformStatusForDisplay(overallStatus).toUpperCase()}`}
            sx={{
              backgroundColor: statusStyle.bgColor,
              color: statusStyle.color,
              border: `2px solid ${statusStyle.borderColor}`,
              fontWeight: "bold",
              fontSize: "0.75rem",
              height: "28px",
              minWidth: "100px",
              boxShadow: `0 2px 8px ${statusStyle.shadowColor}`,
              flexShrink: 0,
              ml: 1,
            }}
          />
        </Box>

        {/* Enhanced Metrics */}
        <Box sx={Styles.collectorMetricsGrid}>
          {collector.type === "EMAIL" ? (
            <>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: colors.blue, fontSize: "1rem" }}>
                  {collector.total_recipients || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
                  <FormattedMessage id="collector.emails" />
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: colors.orange, fontSize: "1rem" }}>
                  {collector.view_count || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
                  <FormattedMessage id="collector.views" />
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: colors.green, fontSize: "1rem" }}>
                  {collector.responses_count || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
                  <FormattedMessage id="collector.responses" />
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: colors.orange, fontSize: "1rem" }}>
                  {collector.view_count || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
                  <FormattedMessage id="collector.views" />
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }} />
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: colors.green, fontSize: "1rem" }}>
                  {collector.responses_count || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
                  <FormattedMessage id="collector.responses" />
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {collector.type === "EMAIL" ? (
          <>
            <Box sx={{ mt: 1, mb: 1, mx: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: "medium", color: "text.secondary" }}>
                  <FormattedMessage id="collector.email_progress" />
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
                    {collector.sent_recipients || 0}/{collector.total_recipients || 0}
                  </Typography>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={
                  collector.total_recipients && collector.sent_recipients !== undefined
                    ? (collector.sent_recipients / collector.total_recipients) * 100
                    : 0
                }
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: "rgba(0,0,0,0.1)",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: statusStyle.borderColor,
                    borderRadius: 2,
                  },
                  mb: 1,
                }}
              />
            </Box>
          </>
        ) : (
          <Box sx={{ gridColumn: "span 3", display: "flex", alignItems: "center", gap: 1, mt: 2, mb: 1, mx: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "'Roboto Mono', monospace",
                fontSize: "0.75rem",
                color: statusStyle.color,
                backgroundColor: statusStyle.bgColor,
                padding: "4px 8px",
                borderRadius: "4px",
                border: `1px solid ${statusStyle.borderColor}`,
                flexGrow: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
              title={collector.link ? `${window.location.origin}${collector.link}` : "Link not available"}
            >
              {collector.link ? `${window.location.origin}${collector.link}` : "Link not available"}
            </Typography>
            <Tooltip title={<FormattedMessage id="collect_list.copy_link" />}>
              <IconButton
                onClick={() => onCopyLink(collector.link)}
                size="small"
                sx={{
                  color: statusStyle.color,
                  backgroundColor: statusStyle.bgColor,
                  "&:hover": {
                    backgroundColor: statusStyle.bgColor.replace("0.1", "0.2"),
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s ease",
                  minWidth: "28px",
                  height: "28px",
                }}
              >
                <ContentCopyIcon sx={{ fontSize: "0.9rem" }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: "space-between", p: 1.5, backgroundColor: "rgba(255,255,255,0.9)" }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip
            title={
              collector.status === "completed" ? (
                <FormattedMessage id="collect_list.survey_closed" defaultMessage="Assessment is closed" />
              ) : collector.is_locked ? (
                <FormattedMessage id="collect_list.unlock_collector" />
              ) : (
                <FormattedMessage id="collect_list.lock_collector" />
              )
            }
          >
            <span>
              <IconButton
                onClick={() => onLockClick(collector)}
                size="small"
                disabled={surveyStatus === "closed" || collector.status === "completed"}
                sx={{
                  color: collector.is_locked ? "error.main" : "primary.main",
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                  "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.26)" },
                }}
              >
                {collector.is_locked ? <LockIcon sx={{ fontSize: "1.1rem" }} /> : <LockOpenIcon sx={{ fontSize: "1.1rem" }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title={<FormattedMessage id="collector.duplicate_collector" defaultMessage="Duplicate" />}>
            <span>
              <IconButton
                onClick={() => onDuplicateClick(collector)}
                size="small"
                disabled={surveyStatus === "closed"}
                sx={{
                  color: "primary.main",
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                  "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.26)" },
                }}
              >
                <DuplicateIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            </span>
          </Tooltip>
          {collector.type === "EMAIL" && (
            <Tooltip title={<FormattedMessage id="collector.send_reminder" defaultMessage="Send Reminder" />}>
              <span>
                <IconButton
                  onClick={() => {
                    resetReminderDialog();
                    setReminderDialogOpen(true);
                  }}
                  size="small"
                  disabled={surveyStatus === "closed" || collector.status === "completed"}
                  sx={{
                    color: "warning.main",
                    "&:hover": { backgroundColor: "rgba(255, 152, 0, 0.04)" },
                    "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.26)" },
                  }}
                >
                  <ReminderIcon sx={{ fontSize: "1.1rem" }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title={<FormattedMessage id="collector.edit_collector" />}>
            <span>
              <IconButton
                onClick={() => onEditClick(collector)}
                size="small"
                disabled={surveyStatus === "closed" || collector.status === "completed" || collector.status === "active"}
                sx={{
                  color: "primary.main",
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                  "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.26)" },
                }}
              >
                <EditIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={<FormattedMessage id="collector.delete_collector" />}>
            <span>
              <IconButton
                onClick={() => onDeleteClick(collector)}
                size="small"
                disabled={surveyStatus === "closed"}
                sx={{
                  color: "error.main",
                  "&:hover": { backgroundColor: "rgba(255, 0, 0, 0.04)" },
                  "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.26)" },
                }}
              >
                <DeleteIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </CardActions>

      <Dialog
        open={reminderDialogOpen}
        onClose={closeReminderDialog}
        PaperProps={{ sx: { borderRadius: "12px", minWidth: "360px", maxWidth: "420px" } }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          <FormattedMessage id="collector.reminder_dialog_title" defaultMessage="Send Reminder" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <FormattedMessage
              id="collector.reminder_dialog_intro"
              defaultMessage="Choose how to remind {count} recipients who have not yet responded."
              values={{
                count: (
                  <strong>
                    {Math.max(0, (collector.total_recipients || 0) - (collector.responses_count || 0))}
                  </strong>
                ),
              }}
            />
            <FormControl component="fieldset" sx={{ mt: 2, width: "100%" }} disabled={isSendingReminder}>
              <RadioGroup
                value={reminderMode}
                onChange={(e) => {
                  const v = e.target.value;
                  setReminderMode(v);
                  if (v === "now") {
                    setScheduledTimeStr(null);
                    setSchedulePickerAnchor(null);
                  }
                }}
              >
                <FormControlLabel
                  value="now"
                  control={<Radio size="small" color="warning" />}
                  label={
                    <FormattedMessage
                      id="collector.reminder_mode_now"
                      defaultMessage="Send now"
                    />
                  }
                />
                <FormControlLabel
                  value="schedule"
                  control={<Radio size="small" color="warning" />}
                  label={
                    <FormattedMessage
                      id="collector.reminder_mode_schedule"
                      defaultMessage="Schedule for later"
                    />
                  }
                />
              </RadioGroup>
            </FormControl>
            {reminderMode === "schedule" && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={isSendingReminder}
                  onClick={(e) => setSchedulePickerAnchor(e.currentTarget)}
                  sx={{ textTransform: "none", mb: scheduledTimeStr ? 1 : 0 }}
                >
                  <FormattedMessage
                    id="collector.reminder_choose_datetime"
                    defaultMessage="Select date and time"
                  />
                </Button>
                {scheduledTimeStr && (
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                    <FormattedMessage
                      id="collector.reminder_scheduled_for"
                      defaultMessage="Scheduled for: {when}"
                      values={{ when: <strong>{scheduledTimeStr}</strong> }}
                    />
                  </Typography>
                )}
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: "wrap" }}>
          <Button onClick={closeReminderDialog} disabled={isSendingReminder} variant="outlined" size="small">
            <FormattedMessage id="collector.reminder_cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            onClick={handleConfirmReminder}
            disabled={
              isSendingReminder || (reminderMode === "schedule" && !scheduledTimeStr)
            }
            variant="contained"
            color="warning"
            size="small"
            startIcon={
              isSendingReminder ? <CircularProgress size={14} color="inherit" /> : <ReminderIcon sx={{ fontSize: "1rem" }} />
            }
          >
            {reminderMode === "schedule" ? (
              <FormattedMessage id="collector.reminder_confirm_schedule" defaultMessage="Schedule" />
            ) : (
              <FormattedMessage id="collector.reminder_confirm" defaultMessage="Send" />
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <ReminderScheduleDateTimePopover
        open={Boolean(schedulePickerAnchor)}
        anchorEl={schedulePickerAnchor}
        onClose={() => setSchedulePickerAnchor(null)}
        onConfirm={(localYmdHm) => {
          setScheduledTimeStr(localYmdHm);
          setSchedulePickerAnchor(null);
        }}
      />
    </Card>
  );
}
